import React from 'react';
import { useUser } from '../context/UserContext';
import { useShift } from '../context/ShiftContext';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
const Dashboard = () => {
  const {
    user,
    users
  } = useUser();
  const {
    shifts
  } = useShift();
  // Get current month's shifts
  const today = new Date();
  const startMonth = startOfMonth(today);
  const endMonth = endOfMonth(today);
  const daysInMonth = eachDayOfInterval({
    start: startMonth,
    end: endMonth
  });
  // Count assignments per user
  const userAssignmentCounts = users.map(u => {
    const nightShifts = shifts.filter(s => s.type === 'night' && s.assignedTo.includes(u.id)).length;
    const weekendShifts = shifts.filter(s => s.type === 'weekend' && s.assignedTo.includes(u.id)).length;
    const holidayShifts = shifts.filter(s => s.type === 'holiday' && s.assignedTo.includes(u.id)).length;
    return {
      id: u.id,
      name: u.name,
      nightShifts,
      weekendShifts,
      holidayShifts
    };
  });
  // Get upcoming assignments for current user
  const userUpcomingShifts = shifts.filter(s => s.assignedTo.includes(user?.id || 0) && new Date(s.date) >= today).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 5);
  // Get available trades
  const availableTrades = shifts.filter(s => s.isAvailableForTrade && new Date(s.date) >= today).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 5);
  return <div className="space-y-8" data-id="element-327">
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-328">
        <h2 className="text-2xl font-bold mb-4 text-gray-800" data-id="element-329">
          Welcome, {user?.name}
        </h2>
        <p className="text-gray-600" data-id="element-330">
          Use the navigation menu to view the calendar, request shifts, or trade
          shifts.
          {user?.isAdmin && ' As an admin, you can also manage APNs and holidays.'}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-331">
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-332">
          <h3 className="text-xl font-bold mb-4 text-gray-800" data-id="element-333">
            Your Upcoming Shifts
          </h3>
          {userUpcomingShifts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-334">
              {userUpcomingShifts.map(shift => <li key={shift.id} className="py-3" data-id="element-335">
                  <div className="flex justify-between" data-id="element-336">
                    <span className="font-medium" data-id="element-337">
                      {format(parseISO(shift.date), 'MMMM d, yyyy')} (
                      {format(parseISO(shift.date), 'EEEE')})
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${shift.type === 'night' ? 'text-white' : shift.type === 'weekend' ? 'bg-purple-100 text-purple-800' : 'text-white'}`} style={shift.type === 'night' ? {
                backgroundColor: '#4772B0'
              } : shift.type === 'holiday' ? {
                backgroundColor: '#FE5757'
              } : {}} data-id="element-338">
                      {shift.type.charAt(0).toUpperCase() + shift.type.slice(1)}
                    </span>
                  </div>
                </li>)}
            </ul> : <p className="text-gray-500" data-id="element-339">You don't have any upcoming shifts.</p>}
        </div>
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-340">
          <h3 className="text-xl font-bold mb-4 text-gray-800" data-id="element-341">
            Available Trades
          </h3>
          {availableTrades.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-342">
              {availableTrades.map(shift => {
            const offeredBy = users.find(u => u.id === shifts.find(s => s.id === shift.id)?.assignedTo);
            return <li key={shift.id} className="py-3" data-id="element-343">
                    <div className="flex justify-between" data-id="element-344">
                      <div data-id="element-345">
                        <span className="font-medium" data-id="element-346">
                          {format(parseISO(shift.date), 'MMMM d, yyyy')}
                        </span>
                        <span className="text-gray-500 ml-2" data-id="element-347">
                          Offered by {offeredBy?.name || 'Unknown'}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${shift.type === 'night' ? 'text-white' : shift.type === 'weekend' ? 'bg-purple-100 text-purple-800' : 'text-white'}`} style={shift.type === 'night' ? {
                  backgroundColor: '#4772B0'
                } : shift.type === 'holiday' ? {
                  backgroundColor: '#FE5757'
                } : {}} data-id="element-348">
                        {shift.type.charAt(0).toUpperCase() + shift.type.slice(1)}
                      </span>
                    </div>
                  </li>;
          })}
            </ul> : <p className="text-gray-500" data-id="element-349">
              No shifts are available for trade right now.
            </p>}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-350">
        <h3 className="text-xl font-bold mb-4 text-gray-800" data-id="element-351">
          Assignment Summary
        </h3>
        <div className="overflow-x-auto" data-id="element-352">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-353">
            <thead data-id="element-354">
              <tr data-id="element-355">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-356">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-357">
                  Night Shifts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-358">
                  Weekend Shifts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-359">
                  Holiday Shifts
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-360">
              {userAssignmentCounts.map(userCount => <tr key={userCount.id} className={user?.id === userCount.id ? 'bg-blue-50' : ''} style={user?.id === userCount.id ? {
              backgroundColor: '#EBF1F9'
            } : {}} data-id="element-361">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900" data-id="element-362">
                    {userCount.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500" data-id="element-363">
                    {userCount.nightShifts}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500" data-id="element-364">
                    {userCount.weekendShifts}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500" data-id="element-365">
                    {userCount.holidayShifts}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
export default Dashboard;